import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import { EventType } from 'src/common/constants/eventTypes'
import { useUser } from 'src/common/hooks/useUser'
import { validatePromotions, validateVoucher } from 'src/services/mainAPI'
import { useAppSelector } from 'src/store/hooks'
import {
    cartRowSelector,
    cartSelector,
    discountSelector,
    promotionSelector,
    selectedItemsSelector,
    shippingSelector,
    subTotalSelector,
    totalPrePromotionSelector,
    totalpreShippingSelector,
    totalSelector,
    vouchersSelector
} from 'src/store/joinevent/selectors'

/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-return */
const selectChallengeAtheleteId = (state) => state?.joinevent?.cart?.challengeAthleteId

const selectedRewards = (state) => state?.joinevent?.selectedRewards || []
const selectedAddons = (state) => state?.joinevent?.selectedAddons || []

const selectChallengeIds = createSelector(cartSelector, (cart) =>
    cart.bundle.challenges.map((item) => item.challengeId)
)

const selectAllSelectedProducts = createSelector(
    selectedRewards,
    selectedAddons,
    (adds, rews) => [...adds, ...rews]
)

const selectShippableProducts = createSelector(selectAllSelectedProducts, (products) =>
    products.filter((item) => item.challengeAddonType.isShipped === true)
)

// flatten array that we create if selectedOptions.length is > 0
const selectConfirmationDisplayProducts = createSelector(
    selectAllSelectedProducts,
    (products) =>
        products.flatMap((item) => {
            if (item.selectedOptions.length > 0) {
                return item.selectedOptions.map((option) => {
                    return {
                        ...item,
                        name: item.name,
                        size: option.displayText
                    }
                })
            }

            return item
        })
)

const selectBundleChallengeItems = createSelector(cartSelector, (cart) => {
    const items = cart.bundle.challenges.flatMap((challenge) => {
        return challenge.items.map((item) => {
            return item
        })
    })

    /*
    // if editing an exisiting order
    if (cart.challengeAthleteId && cart.items?.length) {
        items = cart.items
    }
    */

    return items || []
})

const selectShippableBundleProducts = createSelector(
    selectBundleChallengeItems,
    (products) => products.filter((item) => item?.challengeAddonType?.isShipped === true)
)

export default function useJoinEventWizard() {
    const dispatch = useDispatch()
    const user = useUser()
    const summary = useAppSelector((state) => state.joinevent.summary)
    const rewards = useAppSelector((state) => state.joinevent.rewards || [])
    const eventTitle = useAppSelector((state) => state.joinevent.summary?.name || '')
    const eventId = useAppSelector(
        (state) => state.joinevent?.summary?.challengeUrl || ''
    )
    const eventType = useAppSelector((state) =>
        state.joinevent.summary.isSwag
            ? EventType.Store
            : state.joinevent?.summary?.is30Day
            ? EventType.Challenges
            : EventType.Races
    )
    const addons = useAppSelector((state) => state.joinevent?.addons || [])
    const allSelectedProducts = useAppSelector(selectAllSelectedProducts)
    const shippableProducts = useAppSelector(selectShippableProducts)
    const shippableBundleProducts = useAppSelector(selectShippableBundleProducts)

    const confirmationDisplayProducts = useAppSelector(selectConfirmationDisplayProducts)

    const forPerson = useAppSelector((state) => state.joinevent.cart?.forPerson || '')

    const challengeAtheleteId = useAppSelector(selectChallengeAtheleteId)
    const isNewPurchase = challengeAtheleteId === null

    const cart = useAppSelector(cartSelector)

    const total = useAppSelector(totalSelector)
    const subTotal = useAppSelector(subTotalSelector)
    const shipping = useAppSelector(shippingSelector)
    const discount = useAppSelector(discountSelector)
    const rows = useAppSelector(cartRowSelector)
    const selectedItems = useAppSelector(selectedItemsSelector)
    const vouchers = useAppSelector(vouchersSelector)
    const promotion = useAppSelector(promotionSelector)

    const totalPrePromotion = useAppSelector(totalPrePromotionSelector)
    const totalPreShipping = useAppSelector(totalpreShippingSelector)

    const bundleChallengeItems = useAppSelector(selectBundleChallengeItems)

    const _inBundle = () => {
        return cart.bundle.challenges.some((item) => item.key === summary.challengeId)
    }

    const requireShipping = selectedItems.length > 0
    //Don't show the voucher step if it's an existing order and money has already exchanged hands
    const allowVouchers =
        /*calculatedCart.total > 0 && */ !(
            cart.challengeAthleteId !== null &&
            vouchers !== null &&
            vouchers.length > 0 &&
            vouchers[0].code !== null
        ) && !cart.isApproved
    const showBilling = total > 0 && !cart.isApproved // && !this.props.isAdmin;
    const isRefund = total < 0
    // const authorizedAmountChanged =
    //     cart.paymentMethod === 2 &&
    //     cart.authorizedAmount !== total &&
    //     cart.authorizedAmount > 0 //For PayPal
    const reward = !summary.isSwag ? 'reward' : 'item'
    const included = !summary.isSwag ? ', which will be included in the price.' : '.'
    let rewardAlertMessage = !summary.isAddonOptional
        ? `You must select at least one ${reward}${included}`
        : `${summary.isAddonOptionalReason}, so selecting a Reward is optional.`
    if (summary.addonDiscount > 0 && rewards.length > 1) {
        rewardAlertMessage =
            rewardAlertMessage +
            ` If you select more than one ${reward}, you will receive a ${summary.addonDiscount} percent discount, before shipping costs.`
    }
    const addonAlertMessage =
        'Selecting an add-on is optional. We will ship your selected add-ons to you immediately, we will not wait for you to complete the race.'
    const inBundle = _inBundle()
    const isNew = cart.challengeAthleteId === null
    const isBundle = cart.bundle.challenges.length > 1
    const bundleThankyouMessage = `${summary.name} was purchased in a bundle of ${cart.bundle.challenges.length}. Changes on this screen will only apply to ${summary.name}. To individually start, pause and assign unique devices, and to change the order in which your races will be started, please visit your Dashboard.`
    const challengeIds = useSelector(selectChallengeIds)

    const validatePromotionsCB = useCallback(() => {
        if (
            cart.userId &&
            challengeIds.length > 0 &&
            !(
                cart.challengeAthleteId !== null &&
                typeof promotion !== 'undefined' &&
                promotion !== null &&
                promotion.code !== null
            )
        ) {
            dispatch(
                validatePromotions.initiate(
                    {
                        amount: totalPrePromotion,
                        challengeIds: challengeIds,
                        userId: cart.userId
                    },
                    { subscribe: false, forceRefetch: true }
                )
            )
        }
    }, [
        cart.challengeAthleteId,
        cart.userId,
        challengeIds,
        dispatch,
        promotion,
        totalPrePromotion
    ])

    const validateVoucherCB = useCallback(
        (code: string) => {
            dispatch(
                validateVoucher.initiate(
                    {
                        code: code,
                        amount: totalPreShipping,
                        challengeIds: challengeIds,
                        userId: cart.userId,
                        existingVouchers: vouchers
                    },
                    { subscribe: false, forceRefetch: true }
                )
            )
        },
        [
            cart.challengeAthleteId,
            cart.userId,
            challengeIds,
            dispatch,
            promotion,
            totalPreShipping
        ]
    )

    useEffect(() => {
        if (
            user !== null &&
            user.isAuthenticated &&
            summary.challengeId > 0 &&
            !(
                cart.challengeAthleteId !== null &&
                typeof promotion !== 'undefined' &&
                promotion !== null &&
                promotion.code !== null
            )
        ) {
            dispatch(
                validatePromotions.initiate({
                    amount: totalPrePromotion,
                    challengeIds: challengeIds,
                    userId: cart.userId
                })
            )
        }
    }, [
        cart.challengeAthleteId,
        cart.userId,
        challengeIds,
        dispatch,
        promotion,
        summary.challengeId,
        totalPreShipping,
        user
    ])

    return {
        addons,
        rewards,
        eventTitle,
        eventId,
        eventType,
        summary,
        shippableProducts,
        allSelectedProducts,
        confirmationDisplayProducts,
        forPerson,
        isNewPurchase,
        cart,
        calculatedCart: {
            total: total || 0,
            subTotal,
            shipping,
            discount,
            rows,
            selectedItems,
            vouchers,
            promotion
        },
        isAddonOptional: summary.isAddonOptional,
        requireShipping,
        allowVouchers,
        showBilling,
        isRefund,
        // authorizedAmountChanged,
        rewardAlertMessage,
        addonAlertMessage,
        inBundle,
        isNew,
        isBundle,
        bundleThankyouMessage,
        totalPrePromotion,
        totalPreShipping,
        shippableBundleProducts,
        bundleChallengeItems,
        isAdmin: user?.isAdmin,
        isSwag: summary?.isSwag,
        hasAccessMessage: summary.hasAccessMessage,
        hasAccess: summary.hasAccess,
        validatePromotionsCB,
        validateVoucherCB,
        challengeIds
    }
}
